import { FormBlock, FormCard } from '@qbe/ads-blocks-forms';
import { Button, Form, FormGroupLabel, NumericFormat, Spinner, Typography } from '@qbe/ads-core';
import React, { useEffect } from 'react';
import { Controller } from 'react-hook-form';

import { useAppContent, useAppDispatch, useCoverDetails, useCoverDetailsEffects } from '~/hooks';
import { useFormWithAnalytics } from '~/hooks/useFormWithAnalytics';
import { useTrackOptimizeActivate } from '~/hooks/useTrackOptimizeActivate';
import { BreakdownCover, updateBreakdownCover } from '~/slices/coverDetails';
import { addValidatedCover, removeValidatedCover } from '~/slices/selectCover';
import { qalFormCompleteAction } from '~/thunks/analytics';
import { Cover } from '~/types/cover';
import { isFormStartStarted } from '~/utils/analytics/trackUtils';
import { CurrencyFormat, currencyFormatter } from '~/utils/currencyFormatter';

import { ContextualHelpWithButton } from '../../components-ds/ContextualHelp/ContextualHelpWithButton';

const NumberOfItems = () => {
  const { getTextContent } = useAppContent({ basePath: 'dsme.coverDetailsBreakdown' });

  return (
    <FormGroupLabel
      title={getTextContent('fields.numberOfItems.title')}
      subtitle={getTextContent('fields.numberOfItems.description')}
      alignCenter
      titleSuffix={
        <ContextualHelpWithButton tracking="help: Number of items">
          <Typography>{getTextContent('contextualhelpers.numberOfItems.description')}</Typography>
        </ContextualHelpWithButton>
      }
    />
  );
};
export const CoverDetailsBreakdown = () => {
  const validationRangeRules = {
    sumInsuredMin: 500.001,
    sumInsuredMax: 50000,
    numberOfItemsMin: 1,
    numberOfItemsMax: 20,
    deteriorationOfStockMin: 500.001,
    deteriorationOfStockMax: 20000,
  };

  const { getTextContent: getSharedContent } = useAppContent({
    basePath: 'shared',
  });

  const { getTextContent, getJSXContent } = useAppContent({
    basePath: 'dsme.coverDetailsBreakdown',
    tokens: {
      sumInsuredMin: () =>
        String(currencyFormatter(validationRangeRules.sumInsuredMin, CurrencyFormat.Comma)),
      sumInsuredMax: () =>
        String(currencyFormatter(validationRangeRules.sumInsuredMax, CurrencyFormat.Comma)),
      numberOfItemsMin: () => String(validationRangeRules.numberOfItemsMin),
      numberOfItemsMax: () => String(validationRangeRules.numberOfItemsMax),
      deteriorationOfStockMin: () =>
        String(
          currencyFormatter(validationRangeRules.deteriorationOfStockMin, CurrencyFormat.Comma)
        ),
      deteriorationOfStockMax: () =>
        String(
          currencyFormatter(validationRangeRules.deteriorationOfStockMax, CurrencyFormat.Comma)
        ),
    },
  });
  const { breakdown } = useCoverDetails();
  const { handleSubmit, control, formState, reset } = useFormWithAnalytics<BreakdownCover>({
    mode: 'onChange',
  });
  const dispatch = useAppDispatch();

  const { quotePremiums, isLoading, buttonText } = useCoverDetailsEffects({
    cover: Cover.Breakdown,
  });

  const handleFormComplete = () => {
    if (isFormStartStarted()) dispatch(qalFormCompleteAction());
  };

  useEffect(() => {
    if (breakdown) {
      reset({
        ...breakdown,
        deteriorationOfStock: breakdown.deteriorationOfStock ?? undefined,
      });
    }
  }, [breakdown, reset]);

  useEffect(() => {
    if (formState.isValid) {
      dispatch(addValidatedCover(Cover.Breakdown));
    } else {
      dispatch(removeValidatedCover(Cover.Breakdown));
    }
  }, [dispatch, formState.isValid]);

  const onSubmit = handleSubmit(async (data: BreakdownCover | undefined) => {
    if (!data) {
      console.error('Form data is incomplete');
      return;
    }
    dispatch(updateBreakdownCover(data));
    await quotePremiums();
    handleFormComplete();
  });

  // Tracks the "optimize.activate" event when the component mounts or re-renders.
  // This is used to notify Webtrends (or other analytics tools) for visibility tracking
  // and ensures dynamic content is properly handled.
  useTrackOptimizeActivate();

  return (
    <FormCard variant="sectionTitle" title={getTextContent('title')}>
      <Spinner loading={isLoading} description={getSharedContent('loadingLabel')} />

      <Form onSubmit={onSubmit}>
        <FormGroupLabel
          title={getTextContent('fields.sumInsured.title')}
          subtitle={getTextContent('fields.sumInsured.description')}
          alignCenter
        />

        <FormBlock preset="input" maxWidth="100%" paddingBottom="none">
          <Controller
            control={control}
            render={({ field, fieldState }) => {
              const isInvalid = Boolean(fieldState.error);
              return (
                <NumericFormat
                  id="sumInsured"
                  name={field.name}
                  required
                  prefix="$ "
                  thousandSeparator
                  label={getTextContent('fields.sumInsured.label')}
                  allowNegative={false}
                  onValueChange={(values) => field.onChange(values.floatValue ?? null)}
                  data-track="Enter sum insured amount"
                  data-track-value="mask"
                  error={isInvalid}
                  displayIcon
                  helperText={
                    isInvalid
                      ? (fieldState?.error?.message ?? getTextContent('fields.sumInsured.invalid'))
                      : getTextContent('fields.sumInsured.helperText')
                  }
                  value={
                    field.value !== undefined && field.value !== null ? String(field.value) : ''
                  }
                  inputRef={field.ref}
                />
              );
            }}
            name="sumInsured"
            rules={{
              min: {
                value: validationRangeRules.sumInsuredMin,
                message: getTextContent('fields.sumInsured.invalidMin'),
              },
              max: {
                value: validationRangeRules.sumInsuredMax,
                message: getTextContent('fields.sumInsured.invalidMax'),
              },
              required: true,
            }}
          />
        </FormBlock>

        <NumberOfItems />

        <FormBlock preset="input" maxWidth="100%" paddingBottom="none">
          <Controller
            control={control}
            render={({ field, fieldState }) => {
              const isInvalid = Boolean(fieldState.error);
              return (
                <NumericFormat
                  id="numberOfItems"
                  name={field.name}
                  required
                  thousandSeparator
                  decimalScale={0}
                  label={getTextContent('fields.numberOfItems.label')}
                  value={
                    field.value !== undefined && field.value !== null ? String(field.value) : ''
                  }
                  inputRef={field.ref}
                  allowNegative={false}
                  onValueChange={(values) => field.onChange(values.floatValue ?? null)}
                  data-track={getTextContent('fields.numberOfItems.label')}
                  data-track-value="mask"
                  error={isInvalid}
                  displayIcon
                  helperText={
                    isInvalid
                      ? (fieldState?.error?.message ??
                        getTextContent('fields.numberOfItems.invalid'))
                      : getTextContent('fields.numberOfItems.helperText')
                  }
                />
              );
            }}
            name="numberOfItems"
            rules={{
              min: {
                value: validationRangeRules.numberOfItemsMin,
                message: getTextContent('fields.numberOfItems.invalidMin'),
              },
              max: {
                value: validationRangeRules.numberOfItemsMax,
                message: getTextContent('fields.numberOfItems.invalidMax'),
              },
              required: true,
            }}
          />
        </FormBlock>

        <FormGroupLabel
          title={getTextContent('fields.deteriorationOfStock.title')}
          subtitle={getTextContent('fields.deteriorationOfStock.description')}
          alignCenter
          titleSuffix={
            <ContextualHelpWithButton tracking="help: Deterioration of stock">
              <Typography>
                {getJSXContent('contextualhelpers.deteriorationOfStock.description')}
              </Typography>
            </ContextualHelpWithButton>
          }
        />
        <FormBlock preset="input" maxWidth="100%" paddingBottom="none">
          <Controller
            control={control}
            render={({ field, fieldState }) => {
              const isInvalid = Boolean(fieldState.error);
              return (
                <NumericFormat
                  thousandSeparator
                  id="deteriorationOfStock"
                  name={field.name}
                  label={getTextContent('fields.deteriorationOfStock.label')}
                  prefix="$ "
                  value={
                    field.value !== undefined && field.value !== null ? String(field.value) : ''
                  }
                  allowNegative={false}
                  onValueChange={(values) => field.onChange(values.floatValue ?? null)}
                  data-track="Enter deterioration of stock amount"
                  data-track-value="mask"
                  error={isInvalid}
                  displayIcon
                  inputRef={field.ref}
                  helperText={
                    isInvalid
                      ? (fieldState?.error?.message ??
                        getTextContent('fields.deteriorationOfStock.invalid'))
                      : getTextContent('fields.deteriorationOfStock.helperText')
                  }
                />
              );
            }}
            name="deteriorationOfStock"
            rules={{
              min: {
                value: validationRangeRules.deteriorationOfStockMin,
                message: getTextContent('fields.deteriorationOfStock.invalidMin'),
              },
              max: {
                value: validationRangeRules.deteriorationOfStockMax,
                message: getTextContent('fields.deteriorationOfStock.invalidMax'),
              },
              required: false,
            }}
          />
        </FormBlock>

        <FormBlock preset="button">
          <Button
            label={buttonText}
            type="submit"
            data-track={`${buttonText} - breakdown`}
            disabled={!formState.isValid || isLoading}
          />
        </FormBlock>
      </Form>
    </FormCard>
  );
};

export default CoverDetailsBreakdown;
